import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import NewDesign from 'components/NewDesign';
import { CommonIcons } from 'components/Icons';
import {
  isLocalhost,
  PaymentTabEnum,
  PayViaEnum,
  SIZE_ICON,
  StatusInvoiceEnum,
  tabOfSettings,
  TypeInvoiceEnum,
} from 'consts/data';
import useToggleDialog from 'hooks/useToggleDialog';
import { useHistory, useParams } from 'react-router';
import useGetListTransaction from 'hooks/invoices/useGetListTransaction';
import Loading from 'components/Loading';
import { Alert } from 'reactstrap';
import { showError } from 'helpers/handleError';
// import cloneDeep from 'lodash/cloneDeep';
import { AppRoutes } from 'consts/routesPath';
import UploadDropzone from 'components/UploadDropzone';
import InvoiceServices from 'services/InvoiceServices';
import ConfirmModal from 'components/ConfirmModal';
import { notifySuccess } from 'helpers/handleNotification';
import services from 'services';
import { bytesToMegaBytes } from 'helpers';
import { DOWNLOAD_CREDIT_INVOICE_URL, DOWNLOAD_INVOICE_URL, DOWNLOAD_PROOF_URL } from 'consts/api';
import { genStatusInvoiceColor } from 'consts/colorChart';
import { useTranslation } from 'react-i18next';
import { useQueryParam } from 'hooks/useQueryParam';
import DialogDetailInvoice from 'components/Dialogs/DialogDetailInvoice';
import checkBalanceModel from 'models/check-balance.model';
import usePrevious from 'hooks/usePrevious';
import { useTimeHelpers } from 'providers/TimezoneProvider';

const propTypes = {};

//! Sub component
const EachProof = ({ proof, onRemoveSuccess, hideRemove }) => {
  //! State
  const [openConfirmDelete, toggleConfirmDelete, shouldRenderDelete] = useToggleDialog();
  const auth = services.getLocalStorage();
  const { jwt } = auth;

  const downloadProofURL = () => {
    const hostname = isLocalhost ? 'http://34.102.207.102' : '';
    const link = isLocalhost
      ? '/tb/api/v2/authenticated/user/bank-transfer/pop/download'
      : DOWNLOAD_PROOF_URL;

    return `${hostname}${link}?orderId=${proof.orderId}&proofId=${proof.id}&sessionId=${jwt}`;
  };

  //! Function
  const onClickProof = () =>
    window.open(
      downloadProofURL(),
      '_blank',
      `titlebar=no,location=no,height=1000,width=1366,scrollbars=yes,status=yes`
    );

  const onSubmitRemove = async (_, { toggle, setSubmitting }) => {
    try {
      setSubmitting(true);
      await InvoiceServices.deleteProof({
        proofId: proof.id,
        orderId: proof.orderId,
      });
      onRemoveSuccess && (await onRemoveSuccess());
      notifySuccess('Delete proof successfully!');
      setSubmitting(false);
      toggle();
    } catch (error) {
      setSubmitting(false);
      showError(error);
    }
  };

  //! Render
  return (
    <div className='component:EachProof'>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
        {shouldRenderDelete && (
          <ConfirmModal
            isOpen={openConfirmDelete}
            toggle={toggleConfirmDelete}
            header='Delete proof'
            content={
              <span>
                Are you sure you want to delete proof{' '}
                <b>
                  {proof.id}: {proof.fileName}
                </b>
              </span>
            }
            colorBtnSubmit='danger'
            onSubmit={onSubmitRemove}
          />
        )}

        <NewDesign.Typography variant='link' onClick={onClickProof}>
          {proof?.fileName}
        </NewDesign.Typography>

        {!hideRemove && (
          <CommonIcons.Trash className='invalid-message is-hover' onClick={toggleConfirmDelete} />
        )}
      </div>
    </div>
  );
};

//! Sub component
const MAX_SIZE_PROOF_MB = 16;
const UploadArea = React.memo(({ isProofExist, onSubmitUpload }) => {
  const { t } = useTranslation();
  const filesSelected = useRef([]);
  const [openOverrideUpload, toggleOverrideUpload, shouldRenderOverrideUpload] = useToggleDialog();

  const onUpload = (files) => {
    filesSelected.current = files;

    const isFileLargerThanMax = files.some((file) => {
      return bytesToMegaBytes(file.size) > MAX_SIZE_PROOF_MB;
    });

    if (isFileLargerThanMax) {
      showError(t('shared:file_upload_maximum', { size: `${MAX_SIZE_PROOF_MB}MB` }));
      return;
    }

    if (isProofExist) {
      toggleOverrideUpload();
      return;
    }

    onSubmitUpload && onSubmitUpload(files);
  };

  return (
    <Fragment>
      {shouldRenderOverrideUpload && (
        <ConfirmModal
          isOpen={openOverrideUpload}
          toggle={toggleOverrideUpload}
          header='Confirm upload proof'
          content={
            <NewDesign.Alert color='danger' style={{ margin: 0 }}>
              <NewDesign.Typography component='h5' className='mb-2'>
                Proof of payment already exists!
              </NewDesign.Typography>

              <NewDesign.Typography>
                Uploading new proof will <b>OVERWRITE</b> its current proof.
              </NewDesign.Typography>

              <NewDesign.Typography component='p'>Do you want to upload it?</NewDesign.Typography>
            </NewDesign.Alert>
          }
          onSubmit={() => {
            toggleOverrideUpload();
            onSubmitUpload(filesSelected.current);
          }}
          colorBtnSubmit='danger'
        />
      )}
      <UploadDropzone
        accept='application/pdf'
        onDrop={onUpload}
        label={
          <div>
            <NewDesign.Typography component='p'>
              Drag 'n' drop file PDF here, or click to select file
            </NewDesign.Typography>
            <NewDesign.Typography component='p' style={{ textAlign: 'center' }}>
              Maximum upload file size: {MAX_SIZE_PROOF_MB}MB
            </NewDesign.Typography>
          </div>
        }
      />
    </Fragment>
  );
});

//! Main component
export const DetailInvoice = (props) => {
  //! State
  const { backable = true } = props || {};

  const { showDateTimeByTimezone } = useTimeHelpers();
  const params = useParams();
  const id = params?.id || props?.id;

  const queryParams = useQueryParam();
  const history = useHistory();
  const [refetching, setRefetching] = useState(false);
  const [uploading, setUploading] = useState(false);
  const auth = services.getLocalStorage();
  const { jwt } = auth;
  const isNotEnoughBalance = checkBalanceModel.checkIsNotEnoughCredits();
  const typeInvoiceInitial = queryParams?.type || PayViaEnum.BankTransfer;
  const isDialog = queryParams?.popup === 'true';

  //* Get detail invoice with Bank type
  const initialInvoiceDetail = useMemo(() => {
    return {
      type: typeInvoiceInitial,
      id,
    };
  }, [id, typeInvoiceInitial]);

  const { data, loading, refetch } = useGetListTransaction(initialInvoiceDetail);

  const invoice = data?.result?.[0] || {};
  const type = data?.type || '';
  const status = invoice?.status;
  const isReferral = type === PayViaEnum.Referral;
  const prevStatus = usePrevious(status);

  const isOpen = status === StatusInvoiceEnum.Open;

  const isAllowUploadProof = [StatusInvoiceEnum.WaitPayment, StatusInvoiceEnum.Processing].includes(
    status
  );
  const isProofExist = (invoice?.proofs || []).length > 0;

  useEffect(() => {
    //* Fix breadcrumb
    const breadCrumb = document.querySelector('.breadcrumb');
    if (breadCrumb) {
      breadCrumb.childNodes[2].classList.toggle('d-none');
    }
  }, []);

  useEffect(() => {
    if (isNotEnoughBalance) {
      if (status === StatusInvoiceEnum.Succeeded && prevStatus !== status) {
        window.location.href = AppRoutes.HomePage;
      }
    }
  }, [isNotEnoughBalance, status, prevStatus]);

  //* When user open by popup -> go back to clear URL
  useEffect(() => {
    return () => {
      if (isDialog) {
        history.goBack();
      }
    };
  }, [isDialog]);

  const downloadInvoice = (typeInvoice) => () => {
    const hostname = isLocalhost ? 'http://34.111.225.5' : '';
    const link = isLocalhost
      ? '/tb/api/v2/authenticated/user/invoice/download'
      : DOWNLOAD_INVOICE_URL;

    const url = `${hostname}${link}?id=${invoice?.id}&type=${type}&invoiceType=${typeInvoice}&sessionId=${jwt}`;

    window.open(
      url,
      '_blank',
      `titlebar=no,location=no,height=1000,width=1366,scrollbars=yes,status=yes`
    );
  };

  const downloadCreditInvoice = useCallback(() => {
    const hostname = isLocalhost ? 'http://34.111.225.5' : '';
    const link = isLocalhost
      ? '/tb/api/v2/authenticated/user/invoice/downloadcredinv'
      : DOWNLOAD_CREDIT_INVOICE_URL;

    const url = `${hostname}${link}?orderId=${invoice?.id}&sessionId=${jwt}`;

    window.open(
      url,
      '_blank',
      `titlebar=no,location=no,height=1000,width=1366,scrollbars=yes,status=yes`
    );
  }, [invoice, jwt]);

  //! Function
  const onSubmitUpload = async (files) => {
    try {
      setUploading(true);
      const listReq = files.map((file) =>
        InvoiceServices.uploadProof({ orderId: invoice?.id, file: file })
      );
      await Promise.all(listReq);
      await refetch();
      notifySuccess('Upload proof successfully!');
      setUploading(false);
    } catch (error) {
      setUploading(false);
      showError(error);
    }
  };

  const onRefetch = async () => {
    setRefetching(true);
    await refetch();
    setRefetching(false);
  };

  //! Render
  const information = [
    {
      label: 'User',
      value: invoice?.username || '',
    },
    {
      label: 'Amount',
      value: (
        <Fragment>
          $<NewDesign.NumberFormat value={invoice?.amount || 0} />
        </Fragment>
      ),
    },
    {
      label: 'Created',
      value: invoice?.created ? showDateTimeByTimezone(invoice?.created) : '-',
    },
    !isReferral && {
      label: 'Released',
      value: (
        <Fragment>
          $<NewDesign.NumberFormat value={invoice?.released || 0} />
        </Fragment>
      ),
    },
    {
      label: 'Status',
      value: (
        <Fragment>
          <NewDesign.Typography>{invoice?.status || '-'}</NewDesign.Typography>
          {invoice?.failureMessage && (
            <Alert color='danger' className='mt-2 detail-invoice__failure-message'>
              <NewDesign.Typography>
                <b>Failure Message:</b>
              </NewDesign.Typography>{' '}
              {invoice?.failureMessage || ''}
            </Alert>
          )}
        </Fragment>
      ),
    },
    !isReferral && {
      label: 'Processing fee',
      value: (
        <Fragment>
          $<NewDesign.NumberFormat value={invoice?.processingFee || 0} />
        </Fragment>
      ),
    },
  ].filter((el) => !!el);

  const renderTitle = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} className='mb-4'>
        <NewDesign.Typography component='h2'>Invoice No. {invoice?.id}</NewDesign.Typography>
        <NewDesign.Badge color={genStatusInvoiceColor(status)}>{status}</NewDesign.Badge>

        {refetching ? (
          <Loading />
        ) : (
          <CommonIcons.Refresh className='is-hover' size={SIZE_ICON} onClick={onRefetch} />
        )}
      </div>
    );
  };

  if (loading) {
    return <Loading size='md' />;
  }

  const renderInvoices = () => {
    if (isReferral) {
      return (
        <div className='detail-invoice__invoice mb-4'>
          <NewDesign.Typography component='h4'>Invoice</NewDesign.Typography>
          {isOpen ? (
            <NewDesign.Typography
              variant='link'
              onClick={downloadInvoice(TypeInvoiceEnum.PreInvoice)}
            >
              Download pre-payment invoice
            </NewDesign.Typography>
          ) : (
            <NewDesign.Typography variant='link' onClick={downloadInvoice(TypeInvoiceEnum.Invoice)}>
              Download final invoice
            </NewDesign.Typography>
          )}
        </div>
      );
    }

    return (
      <div className='detail-invoice__invoice mb-4'>
        <NewDesign.Typography component='h4'>Invoice</NewDesign.Typography>
        <NewDesign.Typography variant='link' onClick={downloadInvoice(TypeInvoiceEnum.eInvoice)}>
          Download eInvoice
        </NewDesign.Typography>
        {status === StatusInvoiceEnum.Succeeded ? (
          <NewDesign.Typography variant='link' onClick={downloadInvoice(TypeInvoiceEnum.Invoice)}>
            Download final invoice
          </NewDesign.Typography>
        ) : (
          <NewDesign.Typography
            variant='link'
            onClick={downloadInvoice(TypeInvoiceEnum.PreInvoice)}
          >
            Download pre-payment invoice
          </NewDesign.Typography>
        )}

        {status === StatusInvoiceEnum.Failed && (
          <NewDesign.Typography variant='link' onClick={downloadCreditInvoice}>
            Download credit invoice
          </NewDesign.Typography>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      {backable && (
        <NewDesign.Button
          className='mb-2'
          onClick={() =>
            history.push(
              `${AppRoutes.Settings}?tab=${tabOfSettings.payment}&paymentTab=${PaymentTabEnum.transactions}`
            )
          }
        >
          <CommonIcons.Back /> Back
        </NewDesign.Button>
      )}

      <NewDesign.Card className='component:DetailInvoice detail-invoice'>
        {renderTitle()}

        <div className='detail-invoice__information mb-4'>
          <NewDesign.Typography component='h4'>Information</NewDesign.Typography>
          <div className='detail-invoice__information__grid'>
            {information.map((info) => (
              <div key={info.label} style={info.style}>
                <b>{info.label}:</b> {info.value}
              </div>
            ))}
          </div>
        </div>

        {renderInvoices()}

        <div className='detail-invoice__proof-of-payment mb-4'>
          <div className='d-flex align-items-center' style={{ gap: 12 }}>
            <NewDesign.Typography component='h4' className='mb-2'>
              Proof of payment
            </NewDesign.Typography>

            {uploading && <Loading />}
          </div>

          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {(invoice?.proofs || []).map((el) => (
              <li key={el.id} className='mb-1'>
                <EachProof proof={el} onRemoveSuccess={refetch} hideRemove={true} />
              </li>
            ))}
          </ul>

          {isAllowUploadProof && (
            <UploadArea isProofExist={isProofExist} onSubmitUpload={onSubmitUpload} />
          )}
        </div>

        {/* Hide history - User not allow to see history */}
        {/* <div className='detail-invoice__history'>
          <NewDesign.Typography component='h4'>History</NewDesign.Typography>
          <div className='detail-invoice__history__grid'>
            {cloneDeep(invoice?.history || [])
              .reverse()
              .map((h) => {
                return (
                  <div key={h.date}>
                    <span>{momentInstance(h.date).format('lll')}</span>
                    <span>{h.message}</span>
                  </div>
                );
              })}
          </div>
        </div> */}

        <div className='detail-invoice__actions' />
      </NewDesign.Card>
    </Fragment>
  );
};

const DetailInvoiceWrapper = () => {
  const params = useQueryParam();
  const isDialog = params?.popup === 'true';
  const [openDialogDetail, toggleDialogDetail, shouldRenderDialogDetail] =
    useToggleDialog(isDialog);

  if (isDialog) {
    return (
      <Fragment>
        {shouldRenderDialogDetail && (
          <DialogDetailInvoice isOpen={openDialogDetail} toggle={toggleDialogDetail} />
        )}
      </Fragment>
    );
  }

  return <DetailInvoice />;
};

DetailInvoiceWrapper.propTypes = propTypes;
export default DetailInvoiceWrapper;
